<template>
    <div class="checkout-screen">
        <h2 class="title">
            {{ title }}
        </h2>

        <template v-if="showIntervalSwitch">
            <div class="interval">
                <span class="interval-name" @click="() => setInterval('month')">
                    {{ getIntervalName('month') }}
                </span>

                <ToggleSwitch
                    :disabled="isLoading || isSubmitting"
                    :value="interval === 'year'"
                    @change="(isAnnual) => setInterval(isAnnual ? 'year' : 'month')"
                />

                <span class="interval-name" @click="() => setInterval('year')">
                    {{ getIntervalName('year') }}
                </span>
            </div>
        </template>

        <template v-if="isLoading">
            <BaseLoader class="loader" />
        </template>

        <template v-else>
            <div :class="['invoice-price', { 'invoice-price-discounted': isDiscountedPrice }]">
                <div class="invoice-price-comparison">
                    <div class="invoice-price-subtotal">
                        {{ usdFormatter(price.unitAmount) }}
                    </div>

                    <div v-if="isDiscountedPrice" class="invoice-price-total">
                        {{ usdFormatter(invoice.total) }}
                    </div>
                </div>

                <div v-if="interval === 'year'" class="invoice-price-per-month">
                    (equals to {{ usdFormatter(monthlyPrice) }}/month)
                </div>

                <div v-if="discountCaption" class="invoice-price-per-month">
                    {{ discountCaption }}
                </div>

                <div v-if="trialEnabled" class="trial-info">
                    You will be charged for the selected plan at the end of the 14-day trial unless canceled.
                </div>
            </div>
        </template>

        <CreditCardForm
            v-slot="creditCardHandler"
            :disabled="isSubmitting"
        >
            <CouponCode
                v-if="canApplyCouponCode"
                class="coupon-code"
                :initial-code="promo"
                :billing-plan="plan"
                :validate-immediatelly="false"
                :disabled="isSubmitting"
                @change="(promo) => setPromo(promo)"
            />

            <FormButton
                class="is-purple"
                :disabled="isLoading || isSubmitting"
                :is-loading="isSubmitting"
                text="Confirm and Subscribe"
                @click="handleSubmit(creditCardHandler)"
            />
        </CreditCardForm>
    </div>
</template>
<script>
import apiCall from '@/utils/apiCall';
import CouponCode from '@/components/Billing/CouponCode';
import CreditCardForm from '../CreditCardForm';
import ToggleSwitch from '@/components/Controls/ToggleSwitch';
import usdFormatter from '@/utils/usdFormatter';
import { FormButton } from '@/components/Form';
import { mapActions, mapMutations, mapState } from 'vuex';

const intervals = {
    month: 'Monthly',
    year: 'Annualy',
};

const { Rewardful } = window;

export default {
    components: {
        CouponCode,
        CreditCardForm,
        FormButton,
        ToggleSwitch,
    },

    props: {
        onSubscribe: {
            type: Function,
            required: true,
        },
    },

    data: () => ({
        isSubmitting: false,
        interval: 'year',
        invoice: null,
    }),

    computed: {
        ...mapState(['userCard', 'currentSubscription']),

        ...mapState('show/modal', {
            trialEnabled: (state) => state.trialEnabled ?? false,
            plan: (state) => state.plan ?? {},
            promo: (state) => state.promo,
        }),

        /**
         * Gets the screen title.
         *
         * @returns {string} Screen title.
         */
        title() {
            if (this.trialEnabled) {
                return `${this.plan.name} - 14-Day Trial`;
            }

            return `${this.plan.name} - ${this.getIntervalName(this.interval)}`;
        },

        /**
         * Indicates whether interval switch should be visible or not.
         *
         * @returns {boolean} Check result.
         */
        showIntervalSwitch() {
            return ! this.userHasSubscription;
        },

        /**
         * Gets the price object for selected interval.
         *
         * @returns {object} Price object.
         */
        price() {
            return this.plan.prices[this.interval];
        },

        /**
         * Checks whether invoice price is disounted.
         *
         * @returns {boolean} Check result.
         */
        isDiscountedPrice() {
            if (! this.invoice) {
                return null;
            }

            return this.price.unitAmount !== this.invoice.total;
        },

        /**
         * Gets the coupon applied to the previewed invoice, if any.
         *
         * The invoice is the raw Stripe upcoming invoice, so its keys are
         * snake_case (duration, duration_in_months, percent_off).
         *
         * @returns {?object} Stripe coupon object, or null.
         */
        coupon() {
            return this.invoice?.discount?.coupon ?? null;
        },

        /**
         * Builds the monthly price caption for temporary discounts.
         *
         * A `forever` coupon keeps the discounted price indefinitely, so no
         * "reverts to" caption is shown. `once`/`repeating` coupons apply only
         * to the first month / first N months, after which the full price
         * resumes.
         *
         * @returns {?string} Caption text, or null when none applies.
         */
        discountCaption() {
            if ('month' !== this.interval || ! this.isDiscountedPrice || ! this.coupon) {
                return null;
            }

            if ('forever' === this.coupon.duration) {
                return null;
            }

            const fullPrice = usdFormatter(this.price.unitAmount);
            const months = this.coupon.duration_in_months;

            if ('repeating' === this.coupon.duration && 1 < months) {
                return `(for first ${months} months, then ${fullPrice}/month)`;
            }

            return `(for first month, then ${fullPrice}/month)`;
        },

        /**
         * Indicates whether some data is loading on page.
         *
         * @returns {boolean} Check result.
         */
        isLoading() {
            return this.isLoadingUserCard || this.isInvoiceLoading;
        },

        /**
         * Indicates whether user credit card data is loading on page.
         *
         * @returns {boolean} Check result.
         */
        isLoadingCreditCard() {
            return null === this.userCard;
        },

        /**
         * Indicates whether invoice data is loading on page.
         *
         * @returns {boolean} Check result.
         */
        isInvoiceLoading() {
            return ! this.invoice;
        },

        /**
         * Indicates whether user has subscription.
         *
         * @returns {boolean} Check result.
         */
        userHasSubscription() {
            return !! this.currentSubscription;
        },

        /**
         * Calculate monthly price.
         *
         * @returns {number} Monthly price.
         */
        monthlyPrice() {
            const total = this.isDiscountedPrice ? this.invoice.total : this.price.unitAmount;

            return total / 12;
        },

        /**
         * Indicates whether user is able to apply coupon code.
         *
         * @returns {boolean} Check result
         */
        canApplyCouponCode() {
            if (this.trialEnabled) {
                return false;
            }

            if (! this.userHasSubscription) {
                return false;
            }

            return true;
        },
    },

    watch: {
        /**
         * Watches changes of selected interval.
         */
        interval() {
            this.calculate();
        },

        /**
         * Watches changes of promo.
         */
        promo() {
            this.calculate();
        },

        /**
         * Watches changes of "is submitting" state.
         *
         * @param  {boolean} isSubmitting "is submitting" state
         */
        isSubmitting(isSubmitting) {
            this.setPreviousStepLocked(isSubmitting);
        },
    },

    /**
     * Executed after component is created.
     */
    async created() {
        await this.loadUserSubscription();

        this.interval = this.currentSubscription?.interval ?? 'year';
        this.calculate({ initialCalculation: true });
    },

    methods: {
        usdFormatter,

        ...mapActions(['loadUserSubscription']),

        ...mapMutations('show/modal', [
            'setPromo',
            'setPreviousStepLocked',
            'setCardError',
        ]),

        /**
         * Gets the interval name.
         *
         * @param {string} interval Interval slug.
         * @returns {string} Interval name.
         */
        getIntervalName(interval) {
            return intervals[interval];
        },

        /**
         * Changes change of interval.
         *
         * @param {string} interval Selected interval
         */
        setInterval(interval) {
            this.interval = interval;
        },

        /**
         * Calculate invoice.
         */
        async calculate({ initialCalculation } = {}) {
            if (this.isInvoiceLoading && ! initialCalculation) {
                return;
            }

            this.error = null;
            this.invoice = null;

            const { data, status } = await apiCall(this, 'post', '/api/billing/invoices/calculate', {
                promo: this.promo,
                plan: this.plan.slug,
                price: this.price.id,
                interval: this.interval,
            });

            if (400 <= status) {
                this.error = data.message;
            } else {
                this.invoice = data;
            }
        },

        /**
         * Handles submit.
         *
         * @param  {object} creditCard Credit card handler.
         */
        async handleSubmit(creditCard) {
            this.isSubmitting = true;

            try {
                const paymentMethod = await creditCard.getPaymentMethod();

                if (! this.userHasSubscription) {
                    await this.createSubscription({ paymentMethod });
                } else {
                    if (creditCard.isNew()) {
                        await this.updatePaymentMethod({ paymentMethod });
                    }

                    await this.updateSubscription();
                }
            } catch (e) {
                // if ('credit_card' === e.cause) {
                // this.setCardError(e.message);
                // }

                this.isSubmitting = false;
            }
        },

        /**
         * Creates subscription.
         *
         * @param {object} creditCard Credit card handler
         */
        async createSubscription({ paymentMethod }) {
            const requestData = {
                price_ids: [this.price.id],
                payment_method: paymentMethod,
                rfref: 'undefined' !== typeof Rewardful ? Rewardful.referral : null,
                ...(this.promo ? { promo: this.promo } : {}),
                ...(this.trialEnabled ? { trial: true } : {}),
            };

            const { data, status } = await apiCall(this, 'post', '/api/billing/subscriptions', requestData);

            if (400 <= status) {
                throw new Error(data.message);
            }

            this.onSubscribe({
                ...data,
                wasRecentlyCreated: true,
            });
        },

        /**
         * Updates payment method.
         *
         * @param {object} creditCard Credit card handler
         */
        async updatePaymentMethod({ paymentMethod }) {
            const paymentResponse = await apiCall(
                this,
                'post',
                '/api/billing/payments/methods',
                { payment_method: paymentMethod }
            );

            if (400 <= paymentResponse.status) {
                throw new Error(paymentResponse.data.message);
            }

            const subscriptionResponse = await apiCall(
                this,
                'put',
                `/api/billing/subscriptions/${this.currentSubscription.id}`,
                { payment_method: paymentMethod }
            );

            if (400 <= subscriptionResponse.status) {
                throw new Error(subscriptionResponse.data.message);
            }
        },

        /**
         * Updates subscription
         */
        async updateSubscription() {
            const { status, data } = await apiCall(
                this,
                'put',
                `/api/billing/subscriptions/${this.currentSubscription.id}/products/increment`,
                { price_ids: [this.price.id] }
            );

            if (400 <= status) {
                throw new Error(data.message);
            }

            this.onSubscribe(data);
        },
    },
};
</script>
<style scoped lang="scss">
    @use './CheckoutScreen.scss';
</style>
